import * as React from 'react';

import { DefaultButton, IButton, IconButton } from '@fluentui/react/lib/Button';
import { KeyCodes, getId } from '@fluentui/react/lib/Utilities';
import {
    confirmAndDeleteCalendarGroup,
    createCalendar,
    createGroup,
} from '../actions/calendarGroupActions';
import {
    ellipsisAccesibility,
    newCalendarAccessibility,
    renameGroupAccessibility,
    showLessCalendarsString,
    showMoreCalendarsString,
} from '../strings.locstring.json';
import getCalendarsForCalendarGroupInternal, {
    GetCalendarsListType,
} from '../selectors/getCalendarsForCalendarGroup';
import { startedRenaming, stoppedRenaming } from '../mutators/renamingMutators';

import type { Calendar } from 'owa-calendar-cache';
import type { CalendarGroup } from 'owa-graph-schema';
import CalendarGroupType from 'owa-service/src/contract/CalendarGroupType';
import CalendarNodeTextField from './CalendarNodeTextField';
import CalendarPickerCalendar from './CalendarPickerCalendar';
import CalendarPickerDragAndDroppableItem from './CalendarPickerDragAndDroppableItem';
import CalendarPickerDroppablePlaceholder from './CalendarPickerDroppablePlaceholder';
import CalendarPickerGroupContextMenu from './CalendarPickerGroupContextMenu';
import CalendarPickerScenarioId from '../store/schema/CalendarPickerScenarioId';
import { ControlIcons } from 'owa-control-icons';
import { DraggableItemTypes } from 'owa-dnd';
import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { Icon } from '@fluentui/react/lib/Icon';
import { doesCalendarGroupHaveExpandedCalendars } from '../selectors/expandedCalendarsForCalendarGroupSelectors';
import { expandAccount } from '../mutators/collapseCalendarAccountMutators';
import getAnchorElementForContextMenu from '../utils/getAnchorElementForContextMenu';
import getContextMenuViewStateForItem from '../selectors/getContextMenuViewStateForItem';
import { getFlexContainerStyle } from './getFlexContainerStyle';
import { hideContextMenu } from '../mutators/contextMenuMutators';
import isCalendarBeingDragged from '../selectors/isCalendarBeingDragged';
import isCalendarGroupCollapsed from '../selectors/isCalendarGroupCollapsed';
import isCreatingCalendarForCalendarGroup from '../selectors/isCreatingCalendarForCalendarGroup';
import { isFeatureEnabled } from 'owa-feature-flags';
import isRenamingItem from '../selectors/isRenamingItem';
import { isSelectedCalendar } from 'owa-calendar-surface-calendars-store';
import { lazyLoadPublicFolders } from 'owa-publicfolder-picker';
import loc from 'owa-localize';
import { myCalendarsText } from 'owa-locstrings/lib/strings/mycalendarstext.locstring.json';
import { observer } from 'mobx-react-lite';
import { otherCalendarsText } from 'owa-locstrings/lib/strings/othercalendarstext.locstring.json';
import { peoplesCalendarsText } from 'owa-locstrings/lib/strings/peoplescalendarstext.locstring.json';
import { renameCalendarGroup } from 'owa-calendar-update';
import { showCalendarContextMenu } from '../actions/calendarActions';
import { stoppedCreatingCalendar } from '../mutators/creatingCalendarMutators';
import { toggleCalendarGroup } from '../mutators/collapseCalendarGroupMutators';
import { toggleExpandCalendarsForCalendarGroup } from '../mutators/expandedCalendarsForCalendarGroupMutators';
import { useComputed } from 'owa-react-hooks/lib/useComputed';

const styles = require('./CalendarPicker.scss');
const classNames = require('classnames/bind').bind(styles);

/**
 * The properties for the calendar group picker
 */
export interface CalendarPickerGroupProps {
    scenarioId: CalendarPickerScenarioId;
    className: string;
    calendarGroup: CalendarGroup;
    userIdentity: string;
    hasMultipleAccounts: boolean;
    isCondensed: boolean;
    onNewCalendarGroup: (scenarioId: CalendarPickerScenarioId, userIdentity: string) => void;
    onDeleteCalendarGroup?: () => void;
    showMoreForCalendarGroup?: boolean;
    showGroupContextMenuButton?: boolean;
    showListItemContextMenuButton?: boolean;
    onClickAddTeamsCalendar?: () => void;
    prevCalendarGroupId: string;
    nextCalendarGroupId: string;
}

export interface CalendarPickerGroupHandle {
    focus(): void;
}

/**
 * The calendar picker group component for rendering calendar group headers and the calendars they contain.
 */
export default observer(
    function CalendarPickerGroup(
        props: CalendarPickerGroupProps,
        ref: React.Ref<CalendarPickerGroupHandle>
    ) {
        const buttonRef = React.useRef<IButton>();
        const contextMenuButtonRef = React.useRef<IButton>();
        const calendarRefs = React.useRef<CalendarPickerCalendar[]>([]);
        const [
            contextMenuHasKeyboardFocus,
            setContextMenuHasKeyboardFocus,
        ] = React.useState<boolean>(false);
        const [contextMenuIsActive, setContextMenuIsActive] = React.useState<boolean>(false);
        const displayName = useComputed((): string => {
            const { calendarGroup } = props;
            switch (calendarGroup.GroupType) {
                case CalendarGroupType.MyCalendars:
                    return loc(myCalendarsText);
                case CalendarGroupType.OtherCalendars:
                    return loc(otherCalendarsText);
                case CalendarGroupType.PeoplesCalendars:
                    return loc(peoplesCalendarsText);
                default:
                    return calendarGroup.GroupName;
            }
        });
        const calendarsForCalendarGroup = useComputed(() => {
            const {
                scenarioId,
                userIdentity,
                calendarGroup: { calendarGroupId },
                showMoreForCalendarGroup,
            } = props;
            const isExpanded = doesCalendarGroupHaveExpandedCalendars(
                scenarioId,
                userIdentity,
                calendarGroupId.id
            );
            const listType: GetCalendarsListType = showMoreForCalendarGroup
                ? isExpanded
                    ? GetCalendarsListType.SortedAll
                    : GetCalendarsListType.TopAndSelected
                : isExpanded
                ? GetCalendarsListType.SortedAll
                : GetCalendarsListType.All;
            return getCalendarsForCalendarGroupInternal(
                userIdentity,
                props.calendarGroup,
                listType
            );
        });
        const isEmptyCalendarGroup = useComputed(() => {
            return calendarsForCalendarGroup.get().length === 0;
        });

        React.useImperativeHandle(
            ref,
            () => ({
                focus() {
                    if (buttonRef.current) {
                        buttonRef.current.focus();
                    }
                },
            }),
            []
        );

        /**
         * renders calendar group component when isHidden returns false
         */
        const renderCalendarGroupComponent = (): JSX.Element => {
            const {
                scenarioId,
                userIdentity,
                className,
                calendarGroup: { calendarGroupId, GroupType },
                showMoreForCalendarGroup,
                hasMultipleAccounts,
            } = props;
            const { calendarGroup, onClickAddTeamsCalendar } = props;
            const contextMenu = getContextMenuViewStateForItem(scenarioId, calendarGroupId.id);
            const isCollapsed = isCalendarGroupCollapsed(
                scenarioId,
                userIdentity,
                calendarGroupId.id
            );
            const isCreatingCalendar = isCreatingCalendarForCalendarGroup(
                scenarioId,
                userIdentity,
                calendarGroupId.id
            );
            const calendars = calendarsForCalendarGroup.get();
            const groupHeaderId = getId('calendarGroupHeader');
            const shouldRenderDraggable =
                isFeatureEnabled('cal-calendarPickerReorder') &&
                calendarGroup.calendarGroupId.mailboxInfo.type === 'UserMailbox';

            return (
                <li className={className}>
                    {renderCalendarGroupHeader(groupHeaderId)}
                    {!isCollapsed && (
                        <>
                            <FocusZone
                                className={styles.calendarList}
                                role="listbox"
                                aria-multiselectable="true"
                                aria-labelledby={groupHeaderId}>
                                {shouldRenderDraggable
                                    ? renderDraggableCalendarViews()
                                    : renderCalendarViews()}
                            </FocusZone>
                            {showMoreForCalendarGroup && renderShowMoreOrLessButton()}
                        </>
                    )}
                    {isCreatingCalendar && renderNewCalendar()}
                    <CalendarPickerGroupContextMenu
                        group={calendarGroup}
                        contextMenuViewState={contextMenu}
                        onDismiss={onContextMenuDismiss}
                        enableDeleteCalendarGroup={
                            calendars.length == 0 &&
                            GroupType != CalendarGroupType.MyCalendars &&
                            GroupType != CalendarGroupType.OtherCalendars
                        }
                        enableRenameCalendarGroup={GroupType == CalendarGroupType.Normal}
                        hasMultipleAccounts={hasMultipleAccounts}
                        prevGroupId={props.prevCalendarGroupId}
                        nextGroupId={props.nextCalendarGroupId}
                        onNewGroup={createGroup}
                        onNewCalendarGroup={onNewCalendarGroup}
                        onRenameCalendarGroup={onRenameCalendarGroup}
                        onDeleteCalendarGroup={onDeleteCalendarGroup}
                        onAddPublicFolderToFavorites={onAddPublicFolderToFavorites}
                        enableAddPublicFolderToFavorites={
                            GroupType == CalendarGroupType.OtherCalendars
                        }
                        onNewTeamsCalendar={onClickAddTeamsCalendar}
                    />
                </li>
            );
        };
        /**
         * renders calendar group header or renaming textbox when edited
         */
        const renderCalendarGroupHeader = (groupHeaderId: string): JSX.Element => {
            const {
                scenarioId,
                calendarGroup: { calendarGroupId },
            } = props;
            const isRenaming = isRenamingItem(scenarioId, calendarGroupId.id);
            return isRenaming ? (
                <div className={styles.calendarListItem}>
                    <div
                        className={
                            props.hasMultipleAccounts
                                ? styles.calendarListSubHeaderIcon
                                : styles.calendarListHeaderIcon
                        }>
                        <Icon
                            iconName={ControlIcons.ChevronUp}
                            className={
                                props.hasMultipleAccounts
                                    ? styles.newGroupSmallIcon
                                    : styles.newGroupIcon
                            }
                        />
                    </div>
                    <CalendarNodeTextField
                        className={styles.textboxCalendarGroup}
                        onEntry={onEditCalendarGroupName}
                        onDismiss={onEditCalendarGroupNameDismiss}
                        defaultValue={displayName.get()}
                        ariaLabel={loc(renameGroupAccessibility)}
                    />
                </div>
            ) : (
                getCalendarGroupHeader(groupHeaderId)
            );
        };
        /**
         * render a calendar group header
         */
        const getCalendarGroupHeader = (groupHeaderId: string): JSX.Element => {
            const {
                scenarioId,
                userIdentity,
                calendarGroup: { calendarGroupId },
            } = props;
            const isCollapsed = isCalendarGroupCollapsed(
                scenarioId,
                userIdentity,
                calendarGroupId.id
            );
            const collapseIcon = isCollapsed ? ControlIcons.ChevronRight : ControlIcons.ChevronDown;
            const shouldRenderDraggable =
                isFeatureEnabled('cal-calendarPickerReorder') &&
                props.calendarGroup.calendarGroupId.mailboxInfo.type === 'UserMailbox';

            const calendarGroupHeader = (
                <div
                    className={classNames(styles.listItemContainer, {
                        contextMenuEnabled: props.showGroupContextMenuButton,
                        contextMenuFocused: contextMenuHasKeyboardFocus,
                        contextMenuActive: contextMenuIsActive,
                    })}>
                    <DefaultButton
                        id={groupHeaderId}
                        className={classNames(styles.calendarListHeaderButton, {
                            isMainHeader: !props.hasMultipleAccounts,
                        })}
                        styles={getFlexContainerStyle()}
                        onClick={onCalendarGroupHeaderClick}
                        onContextMenu={onContextMenu}
                        onKeyDown={onOnKeyDown}
                        aria-expanded={!isCollapsed}
                        title={displayName.get()}
                        componentRef={setButtonRef}>
                        <div
                            className={
                                props.hasMultipleAccounts
                                    ? styles.calendarListSubHeaderIcon
                                    : styles.calendarListHeaderIcon
                            }>
                            <Icon className={'flipForRtl'} iconName={collapseIcon} />
                        </div>
                        <span className={styles.listHeaderLabel}>{displayName.get()}</span>
                    </DefaultButton>
                    {props.showGroupContextMenuButton && (
                        <IconButton
                            className={styles.listHeaderContextMenuButton}
                            ariaLabel={loc(ellipsisAccesibility)}
                            title={loc(ellipsisAccesibility)}
                            tabIndex={-1}
                            iconProps={{ iconName: ControlIcons.More }}
                            onClick={onContextMenuClick}
                            componentRef={setContextMenuButtonRef}
                            onKeyDown={contextMenuOnKeyDown}
                            onFocus={contextMenuButtonOnFocus}
                            onBlur={contextMenuButtonOnBlur}
                        />
                    )}
                </div>
            );

            return shouldRenderDraggable ? (
                <CalendarPickerDragAndDroppableItem
                    itemId={props.calendarGroup.serverGroupId}
                    itemType={DraggableItemTypes.CalendarGroupNode}
                    scenarioId={props.scenarioId}
                    userIdentity={props.userIdentity}
                    displayName={displayName.get()}
                    previousItemId={props.prevCalendarGroupId}>
                    {calendarGroupHeader}
                </CalendarPickerDragAndDroppableItem>
            ) : (
                calendarGroupHeader
            );
        };
        /**
         * renders calendar nodes under the calendar group
         */
        const renderCalendarViews = (): JSX.Element[] => {
            const calendars = calendarsForCalendarGroup.get();
            return calendars.map(calendar => renderCalendarView(calendar));
        };
        /**
         * Renders calendar nodes under the calendar group as drag and droppable items.
         */
        const renderDraggableCalendarViews = (): JSX.Element[] => {
            const calendars = calendarsForCalendarGroup.get();
            let prevCalendarId: string;
            const components: JSX.Element[] = [];

            for (let index = 0; index < calendars.length; index++) {
                const calendarId = calendars[index].id.id;
                const nextCalendarId =
                    index + 1 < calendars.length ? calendars[index + 1].id.id : null;

                components.push(
                    <CalendarPickerDragAndDroppableItem
                        key={calendarId}
                        itemId={calendarId}
                        itemType={DraggableItemTypes.CalendarNode}
                        userIdentity={props.userIdentity}
                        displayName={calendars[index].displayName}
                        parentGroupId={props.calendarGroup.serverGroupId}
                        scenarioId={props.scenarioId}
                        previousItemId={prevCalendarId}>
                        {renderCalendarView(calendars[index], prevCalendarId, nextCalendarId)}
                    </CalendarPickerDragAndDroppableItem>
                );
                prevCalendarId = calendarId;
            }

            if (isCalendarBeingDragged(props.scenarioId)) {
                components.push(
                    <CalendarPickerDroppablePlaceholder
                        itemType={DraggableItemTypes.CalendarNode}
                        previousItemId={prevCalendarId}
                        parentGroupId={props.calendarGroup.serverGroupId}
                        userIdentity={props.userIdentity}
                        scenarioId={props.scenarioId}
                    />
                );
            }

            return components;
        };
        /**
         * renders calendar node under the calendar group
         */
        const renderCalendarView = (
            calendar: Calendar,
            prevCalendarId?: string,
            nextCalendarId?: string
        ): JSX.Element => {
            const calendarId = calendar.id.id;
            return (
                <CalendarPickerCalendar
                    scenarioId={props.scenarioId}
                    key={calendarId}
                    ref={ref => {
                        calendarRefs.current[calendarId] = ref;
                    }}
                    selected={isSelectedCalendar(calendar)}
                    calendar={calendar}
                    onDeleteCalendar={onDeleteCalendar}
                    showContextMenuButton={
                        props.scenarioId !== CalendarPickerScenarioId.AddCalendarModal &&
                        props.showListItemContextMenuButton
                    }
                    hasMultipleAccounts={props.hasMultipleAccounts}
                    isCondensed={props.isCondensed}
                    prevCalendarId={prevCalendarId}
                    nextCalendarId={nextCalendarId}
                />
            );
        };
        /**
         * Renders new calendar node
         */
        const renderNewCalendar = (): JSX.Element => {
            const isNested = props.hasMultipleAccounts && !props.isCondensed;
            return (
                <div
                    className={classNames(styles.calendarListItem, {
                        indentRight: isNested,
                    })}>
                    <Icon
                        iconName={ControlIcons.Event}
                        className={styles.defaultButtonHeaderIcon}
                    />
                    <CalendarNodeTextField
                        className={classNames(styles.textboxCalendarName, {
                            smallIconMargin: isNested,
                            shortenField: !isNested,
                        })}
                        onEntry={onNewCalendarName}
                        onDismiss={onNewCalendarDismiss}
                        ariaLabel={loc(newCalendarAccessibility)}
                    />
                </div>
            );
        };
        const renderShowMoreOrLessButton = (): JSX.Element => {
            const {
                scenarioId,
                userIdentity,
                calendarGroup: { calendarGroupId },
            } = props;
            const text = doesCalendarGroupHaveExpandedCalendars(
                scenarioId,
                userIdentity,
                calendarGroupId.id
            )
                ? loc(showLessCalendarsString)
                : loc(showMoreCalendarsString);
            return (
                <DefaultButton
                    styles={getFlexContainerStyle()}
                    className={styles.calendarListDefaultButton}
                    title={text}
                    onClick={onShowMoreOrLessButtonClick}>
                    <span className={styles.showMoreOrLessLabel}>{text}</span>
                </DefaultButton>
            );
        };
        /**
         * Called when group header is clicked. Triggers collapse / expand of group panel
         */
        const onCalendarGroupHeaderClick = () => {
            toggleCalendarGroup(
                props.scenarioId,
                props.userIdentity,
                props.calendarGroup.calendarGroupId.id
            );
        };
        const onShowMoreOrLessButtonClick = () => {
            toggleExpandCalendarsForCalendarGroup(
                props.scenarioId,
                props.userIdentity,
                props.calendarGroup.calendarGroupId.id
            );
        };
        /**
         * Called when calendar group rename textbox returns a new group name
         */
        const onEditCalendarGroupName = async (newName: string) => {
            stoppedRenaming(props.scenarioId);
            await renameCalendarGroup(props.userIdentity, props.calendarGroup, newName);
            focus();
        };
        /**
         * Callback for when a calendar in the group is deleted
         */
        const onDeleteCalendar = () => {
            const calendars = calendarsForCalendarGroup.get();
            if (calendars.length > 0) {
                calendarRefs.current[calendars[0].id.id].focus();
            }
        };
        /**
         * Called when group rename textbox is dismissed
         */
        const onEditCalendarGroupNameDismiss = () => {
            stoppedRenaming(props.scenarioId);
            focus();
        };
        /**
         * Called when new calendar textbox returns a new calendar name
         */
        const onNewCalendarName = (newName: string) => {
            stoppedCreatingCalendar(
                props.scenarioId,
                props.userIdentity,
                props.calendarGroup.calendarGroupId.id
            );
            createCalendar(props.userIdentity, newName, props.calendarGroup, createdCalendar => {
                if (createdCalendar) {
                    calendarRefs.current[createdCalendar.id.id].focus();
                }
            });
        };
        /**
         * Called when new calendar textbox is dismissed
         */
        const onNewCalendarDismiss = () => {
            stoppedCreatingCalendar(
                props.scenarioId,
                props.userIdentity,
                props.calendarGroup.calendarGroupId.id
            );
        };
        const onOnKeyDown = (ev: React.KeyboardEvent<unknown>) => {
            if (props.showGroupContextMenuButton) {
                switch (ev.which) {
                    case KeyCodes.right:
                        ev.stopPropagation();
                        contextMenuButtonRef.current.focus();
                        break;
                }
            }
        };
        const contextMenuOnKeyDown = (ev: React.KeyboardEvent<unknown>) => {
            if (props.showGroupContextMenuButton) {
                switch (ev.which) {
                    case KeyCodes.left:
                        ev.stopPropagation();
                        buttonRef.current.focus();
                        break;
                }
            }
        };
        /**
         * Called when group context menu is dismissed
         */
        const onContextMenuDismiss = () => {
            setContextMenuIsActive(false);
            hideContextMenu(props.scenarioId);
        };
        /**
         * Context menu handler for calendar group.  When the onHover context menu entry point
         * button is invoked, we won't want the parent button invoked as well.
         */
        const onContextMenuClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
            ev.stopPropagation();
            onContextMenu(ev);
        };
        /**
         * Context menu handler for calendar group
         */
        const onContextMenu = (ev: React.MouseEvent<HTMLButtonElement>) => {
            ev.preventDefault();
            setContextMenuIsActive(true);
            showCalendarContextMenu(
                props.scenarioId,
                props.calendarGroup.calendarGroupId.id,
                getAnchorElementForContextMenu(ev)
            );
        };
        const onNewCalendarGroup = () => {
            const { scenarioId, userIdentity } = props;
            expandAccount(scenarioId, userIdentity);
            props.onNewCalendarGroup(scenarioId, userIdentity);
        };
        /**
         * Context menu handler for renaming calendar group
         */
        const onRenameCalendarGroup = () => {
            startedRenaming(props.scenarioId, props.calendarGroup.calendarGroupId.id);
        };
        /**
         * Context menu handler for deleting calendar group
         */
        const onDeleteCalendarGroup = () => {
            confirmAndDeleteCalendarGroup(props.userIdentity, props.calendarGroup, isDeleted => {
                if (isDeleted) {
                    props.onDeleteCalendarGroup();
                }
            });
        };
        const setButtonRef = (button: IButton) => {
            buttonRef.current = button;
        };
        const setContextMenuButtonRef = (button: IButton) => {
            contextMenuButtonRef.current = button;
        };
        const contextMenuButtonOnFocus = () => {
            setContextMenuHasKeyboardFocus(true);
        };
        const contextMenuButtonOnBlur = () => {
            setContextMenuHasKeyboardFocus(false);
        };
        const {
            calendarGroup: { GroupType },
        } = props;
        const isHidden =
            isEmptyCalendarGroup.get() &&
            (GroupType == CalendarGroupType.OtherCalendars ||
                GroupType == CalendarGroupType.PeoplesCalendars);
        return !isHidden ? renderCalendarGroupComponent() : null;
    },
    { forwardRef: true }
);

/**
 * Context menu handler for Add Public Folder To Favorites
 */
async function onAddPublicFolderToFavorites() {
    const loadPublicFolders = await lazyLoadPublicFolders.import();
    loadPublicFolders('publicfoldersroot');
}
